import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from '../ui/use-toast';
import { useAuth } from '../../context/AuthContext';
import { FeedbackSubmission, FeedbackType, AppName } from '../../types/feedback';
import { Upload, Image as ImageIcon, X, AlertCircle } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../config/firebase';

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appName: AppName;
}

interface UploadedImage {
  file: File;
  url: string;
  uploading: boolean;
  error?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGES = 3;


export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  onOpenChange,
  appName
}) => {
  const { user } = useAuth();
  const [type, setType] = useState<FeedbackType | ''>('');
  const [description, setDescription] = useState('');
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateImageFile = (file: File): { valid: boolean; error?: string } => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: 'Please upload a valid image file (JPEG, PNG, GIF, or WebP)'
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: 'Image must be less than 5MB'
      };
    }

    return { valid: true };
  };

  const uploadImageToStorage = useCallback(async (file: File): Promise<string> => {
    console.log('🚀 Starting image upload for file:', file.name, 'Size:', file.size, 'Type:', file.type);
    
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    // Sanitize app name to remove spaces and special characters
    const sanitizedAppName = appName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    // Also sanitize the file name to avoid issues with special characters
    const sanitizedFileName = file.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
    const filename = `feedback/${sanitizedAppName}/${timestamp}_${randomId}_${sanitizedFileName}`;
    
    console.log('📁 Upload path:', filename);
    console.log('🏷️ Sanitized app name:', sanitizedAppName);
    console.log('📄 Sanitized file name:', sanitizedFileName);
    
    const imageRef = ref(storage, filename);
    console.log('📤 Uploading to Firebase Storage...');
    
    const snapshot = await uploadBytes(imageRef, file);
    console.log('✅ Upload successful, getting download URL...');
    
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('🔗 Download URL generated:', downloadURL);
    
    return downloadURL;
  }, [appName]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (uploadedImages.length + files.length > MAX_IMAGES) {
      toast({
        title: 'Too many images',
        description: `Maximum ${MAX_IMAGES} images allowed`,
        variant: 'destructive'
      });
      return;
    }

    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast({
          title: 'Invalid file',
          description: validation.error,
          variant: 'destructive'
        });
        continue;
      }

      // Add file to uploading state
      const newImage: UploadedImage = {
        file,
        url: '',
        uploading: true
      };

      setUploadedImages(prev => [...prev, newImage]);

      try {
        console.log('🔄 Processing upload for:', file.name);
        // Upload to Firebase Storage
        const downloadUrl = await uploadImageToStorage(file);
        
        console.log('✅ Upload completed successfully for:', file.name);
        // Update with successful URL
        setUploadedImages(prev =>
          prev.map(img =>
            img.file === file
              ? { ...img, url: downloadUrl, uploading: false }
              : img
          )
        );
      } catch (error) {
        console.error('❌ Upload error for file:', file.name, error);
        
        // Update with error
        setUploadedImages(prev =>
          prev.map(img =>
            img.file === file
              ? { ...img, uploading: false, error: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}` }
              : img
          )
        );
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadedImages.length, uploadImageToStorage]);

  const handleRemoveImage = async (index: number) => {
    const imageToRemove = uploadedImages[index];
    
    // Delete from Firebase Storage if uploaded
    if (imageToRemove.url) {
      try {
        // Extract the storage path from the download URL
        const urlParts = imageToRemove.url.split('/o/')[1]?.split('?')[0];
        if (urlParts) {
          const storagePath = decodeURIComponent(urlParts);
          const imageRef = ref(storage, storagePath);
          await deleteObject(imageRef);
          console.log('✅ Successfully deleted image from storage:', storagePath);
        }
      } catch (error) {
        console.error('❌ Error deleting image from storage:', error);
      }
    }

    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!type) {
      newErrors.type = 'Please select a feedback type';
    }

    if (!description.trim()) {
      newErrors.description = 'Please provide a description';
    } else if (description.trim().length < 10) {
      newErrors.description = 'Please provide at least 10 characters';
    }

    // Check for upload errors
    const hasUploadErrors = uploadedImages.some(img => img.error);
    if (hasUploadErrors) {
      newErrors.images = 'Please resolve image upload errors';
    }

    // Check for pending uploads
    const hasPendingUploads = uploadedImages.some(img => img.uploading);
    if (hasPendingUploads) {
      newErrors.images = 'Please wait for images to finish uploading';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitFeedback = async (submission: FeedbackSubmission): Promise<void> => {
    try {
      // Use the feedbackService instead of direct HTTP calls
      const { feedbackService } = await import('../../services/feedbackService');
      await feedbackService.submitFeedback(submission);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submission: FeedbackSubmission = {
        type: type as FeedbackType,
        message: description.trim(),
        appName,
        userEmail: user.email || '',
        userName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        imageUrls: uploadedImages
          .filter(img => img.url && !img.error)
          .map(img => img.url)
      };

      await submitFeedback(submission);

      toast({
        title: 'Feedback submitted successfully',
        description: 'Thank you for your feedback! We\'ll review it shortly.',
        variant: 'default'
      });

      // Reset form - pass true to indicate successful submission
      handleClose(true);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Submission failed',
        description: 'There was an error submitting your feedback. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (wasSuccessfulSubmission = false) => {
    // Only clean up uploaded images if the submission was NOT successful
    if (!wasSuccessfulSubmission) {
      console.log('🧹 Cleaning up unused images due to modal close without submission');
      uploadedImages.forEach(async (img) => {
        if (img.url) {
          try {
            // Extract the storage path from the download URL
            const urlParts = img.url.split('/o/')[1]?.split('?')[0];
            if (urlParts) {
              const storagePath = decodeURIComponent(urlParts);
              const imageRef = ref(storage, storagePath);
              await deleteObject(imageRef);
              console.log('🧹 Cleaned up unused image:', storagePath);
            }
          } catch (error) {
            console.error('❌ Error cleaning up image:', error);
          }
        }
      });
    } else {
      console.log('✅ Keeping uploaded images - feedback was submitted successfully');
    }

    // Reset form state
    setType('');
    setDescription('');
    setUploadedImages([]);
    setErrors({});
    onOpenChange(false);
  };

  const openFileDialog = () => {
    if (uploadedImages.length < MAX_IMAGES) {
      fileInputRef.current?.click();
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => handleClose(false)}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 dark:text-white border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="dark:text-white">
              Send Feedback - {appName}
            </DialogTitle>
            <DialogDescription className="dark:text-gray-300">
              Help us improve by sharing your feedback, reporting bugs, or suggesting new features.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            {/* Feedback Type */}
            <div className="space-y-2">
              <Label htmlFor="feedback-type" className="dark:text-white">
                Type *
              </Label>
              <Select value={type} onValueChange={(value) => setType(value as FeedbackType)}>
                <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                  <SelectValue placeholder="Select feedback type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bug Report">Bug Report</SelectItem>
                  <SelectItem value="Feature Request">Feature Request</SelectItem>
                  <SelectItem value="Feedback">General Feedback</SelectItem>
                </SelectContent>
              </Select>
              {errors.type && (
                <p className="text-sm text-red-500 dark:text-red-400">{errors.type}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="dark:text-white">
                Description *
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please describe your feedback, bug report, or feature request in detail..."
                className="min-h-[120px] dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                maxLength={2000}
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{errors.description && <span className="text-red-500">{errors.description}</span>}</span>
                <span>{description.length}/2000</span>
              </div>
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="dark:text-white">
                Screenshots (Optional)
              </Label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Upload up to {MAX_IMAGES} images to help illustrate your feedback (max 5MB each)
              </p>

              {/* Upload Button */}
              <Button
                type="button"
                variant="outline"
                onClick={openFileDialog}
                disabled={uploadedImages.length >= MAX_IMAGES}
                className="w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white hover:border-gray-400 dark:hover:border-gray-500"
              >
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-6 w-6 text-gray-400" />
                  <span className="text-sm">
                    {uploadedImages.length >= MAX_IMAGES 
                      ? `Maximum ${MAX_IMAGES} images reached`
                      : 'Click to upload images'
                    }
                  </span>
                </div>
              </Button>

              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                onChange={handleFileSelect}
                className="hidden"
              />

              {errors.images && (
                <p className="text-sm text-red-500 dark:text-red-400 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.images}
                </p>
              )}

              {/* Uploaded Images */}
              {uploadedImages.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  {uploadedImages.map((image, index) => (
                    <div key={index} className="relative border rounded-lg p-3 bg-gray-50 dark:bg-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          {image.uploading ? (
                            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                            </div>
                          ) : image.error ? (
                            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded flex items-center justify-center">
                              <AlertCircle className="h-6 w-6 text-red-500" />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded flex items-center justify-center">
                              <ImageIcon className="h-6 w-6 text-green-500" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {image.file.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {(image.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                          {image.uploading && (
                            <p className="text-xs text-blue-500">Uploading...</p>
                          )}
                          {image.error && (
                            <p className="text-xs text-red-500">{image.error}</p>
                          )}
                          {image.url && !image.error && (
                            <p className="text-xs text-green-500">Upload complete</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveImage(index)}
                          className="flex-shrink-0 h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={isSubmitting}
              className="dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 dark:border-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !type || !description.trim()}
              className="dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};